import classnames from 'classnames';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { trackMixpanelPageView } from 'helpers/post';
import {
  postEventAccountFieldValidate,
  registrationFieldUpdate,
  registrationPostEvent,
  setRole,
} from 'redux/ducks/Registration';
import CustomCheckBox from 'components/V2/CustomInputs/CustomCheckBox';
import './SignUp.scss';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import Google from 'components/OAuth/Google';
import Microsoft from 'components/OAuth/Microsoft';
import { requireOrganizationData } from 'helpers/dashboard';
import {
  GOOGLE_ADS_CLICK_IDENTIFIERS,
  mapQuerySearchParams,
  QUERY_SEARCH_STRING,
  validateQuerySearchString,
  validGoogleAdsParams,
} from '@helpers/localStorage';
import { ORGANIZATION_TYPES, ORGANIZATION_TYPES_REGISTRATION } from '@helpers/enums';
import OrganizationTypeSection from './OrganizationTypeSection';
import { useQueryParamCapture } from '@helpers/useQueryParamCapture';

const validateFieldsNames = ['firstName', 'lastName', 'email', 'password', 'passwordConfirmation', 'terms'];

const SignUp = ({
  history,
  updateField,
  user,
  validateField,
  validation,
  setRole,
  registrationPostEvent,
  ...props
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const orgType = localStorage.getItem('registrationOrgType');

  // Automatically capture query parameters (whitelisted route)
  useQueryParamCapture();

  useEffect(() => {
    setRole('EventOperator');
    updateField('postEventMasterType', '');
    updateField('terms', true);
    // orgType is now handled automatically by useQueryParamCapture hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRole, updateField]);

  useEffect(() => {
    trackMixpanelPageView(`/create-account/sign-up/orgType=${orgType}`, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchData = JSON.parse(localStorage.getItem(QUERY_SEARCH_STRING));
    if (!searchData || searchData?.searchParamsSet) return;

    const params = new URLSearchParams(searchData.searchParams);
    params.forEach((value, key) => {
      const newKey = mapQuerySearchParams[key];

      if (validateQuerySearchString(newKey, value)) {
        const newValue =
          newKey === 'defaultEventSettingId'
            ? ORGANIZATION_TYPES.find(({ name }) => name === value)?.id
            : value;
        updateField(newKey, newValue);
      }
    });

    localStorage.setItem(
      QUERY_SEARCH_STRING,
      JSON.stringify({
        ...searchData,
        searchParamsSet: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFieldValidate = field => {
    validateField(field);
  };

  const onCreateAccount = () => {
    window.grecaptcha.ready(function () {
      console.log('grecaptcha ready');
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3, { action: 'signup' })
        .then(function (token) {
          updateField('token', token);
          registrationPostEvent(history, '/v2/create-event/organization');
        });
    });
  };

  const onNext = e => {
    e.preventDefault();
    validateFieldsNames.forEach(field => {
      onFieldValidate(field);
    });

    // Check if all required fields are filled
    const hasErrors =
      validation.errors.firstName ||
      validation.errors.lastName ||
      validation.errors.email ||
      validation.errors.password ||
      validation.errors.passwordConfirmation;

    if (
      !hasErrors &&
      user.firstName &&
      user.lastName &&
      user.email &&
      user.password &&
      user.passwordConfirmation &&
      user.terms
    ) {
      updateField('defaultEventSettingId', ORGANIZATION_TYPES_REGISTRATION[orgType]);
      onCreateAccount();
    }
  };

  const onFieldChange = (field, triggerValidate) => {
    return ev => {
      updateField(field, ev.currentTarget.value);
      if (triggerValidate) {
        onFieldValidate(field);
      }
    };
  };

  const onTermsChanged = e => {
    updateField('terms', e.target.checked);
    validateField('terms');
  };

  const hyperlinkText = () => {
    return (
      <React.Fragment>
        By creating an account I have read and agree to the{'\n'}{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://go4.io/privacy-policy/">
          Privacy Policy
        </a>
        {', '}{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://go4.io/terms-and-conditions/">
          Terms and Conditions
        </a>{' '}
        {', '}{' '}
        <a href=" https://stripe.com/legal/connect-account" target="_blank" rel="noopener noreferrer">
          Stripe Connected Account Agreement
        </a>{' '}
        and{' '}
        <a href=" https://www.go4.io/go4-marketplace-standards/  " target="_blank" rel="noopener noreferrer">
          Go4 Cancellation Policy.
        </a>
      </React.Fragment>
    );
  };

  return (
    <div className="row v2SignUpScene gotham-regular-font">
      <div className="col-md-7">
        <div className="sign-up-form-section">
          <header>
            <div className="info-text gotham-bold-font">Sign Up to find Athletic Trainers</div>
          </header>
          {props.flash.message && props.flash.type === 'error' && (
            <div className="alert alert-danger alert-margin" role="alert">
              {props.flash.message}
            </div>
          )}
          <div className="oauth-container mt-5 mb-4">
            {process.env.REACT_APP_MICROSOFT_OAUTH_ENABLED === 'true' ? (
              <div className="row">
                <div className="col-lg-6 gotham-medium-font mb-2 mb-lg-0">
                  <Google />
                </div>
                <div className="col-lg-6 gotham-medium-font mt-2 mt-lg-0">
                  <Microsoft />
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-lg-12 gotham-medium-font mb-2 mb-lg-0">
                  <Google />
                </div>
              </div>
            )}
          </div>
          <div className="or-divider">
            <div className="divider-line"></div>
            <span>OR</span>
            <div className="divider-line"></div>
          </div>
          <form
            id="accountForm"
            className="v2signupForm nimbus-regular-font"
            noValidate
            onSubmit={e => e.preventDefault()}
          >
            <div className="row">
              <div className="col-lg-6">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={classnames('form-control', {
                        'is-invalid': validation.errors.firstName,
                      })}
                      name="firstName"
                      id="firstName"
                      value={user.firstName}
                      onChange={onFieldChange('firstName')}
                      onBlur={onFieldValidate.bind(this, 'firstName')}
                    />
                  </div>
                  {validation.errors.firstName && (
                    <div className="invalid-feedback">{validation.errors.firstName}</div>
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={classnames('form-control', {
                        'is-invalid': validation.errors.lastName,
                      })}
                      name="lastName"
                      id="lastName"
                      value={user.lastName}
                      onChange={onFieldChange('lastName')}
                      onBlur={onFieldValidate.bind(this, 'lastName')}
                    />
                  </div>
                  {validation.errors.lastName && (
                    <div className="invalid-feedback">{validation.errors.lastName}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-group">
                <input
                  type="email"
                  className={classnames('form-control', {
                    'is-invalid': validation.errors.email,
                  })}
                  name="email"
                  id="email"
                  value={user.email}
                  onChange={onFieldChange('email')}
                  onBlur={onFieldValidate.bind(this, 'email')}
                />
              </div>
              {validation.errors.email && <div className="invalid-feedback">{validation.errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={classnames('form-control password-input', {
                    'is-invalid': validation.errors.password,
                  })}
                  value={user.password}
                  name="password"
                  id="password"
                  placeholder={'Password must be 8+ characters'}
                  onChange={e => {
                    onFieldChange('password')(e);
                    updateField('passwordConfirmation', e.currentTarget.value);
                  }}
                  onBlur={onFieldValidate.bind(this, 'password')}
                />
                <i
                  className={`show-password fa ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                  aria-hidden="true"
                  onClick={() => setShowPassword(!showPassword)}
                ></i>
              </div>
              {validation.errors.password && (
                <div className="invalid-feedback">{validation.errors.password}</div>
              )}
            </div>

            <CustomCheckBox
              id="terms"
              name="terms"
              checked={!!user.terms}
              onClick={onTermsChanged}
              className="terms-checkbox pl-0 hide-checkbox"
              center
            >
              {hyperlinkText()}
            </CustomCheckBox>

            <button
              type="button"
              onClick={onNext}
              className={classnames('btn plain-button gotham-bold-font signup', {
                disabled:
                  !user.firstName ||
                  !user.lastName ||
                  !user.email ||
                  !user.password ||
                  !user.passwordConfirmation ||
                  !user.terms,
              })}
            >
              CREATE ACCOUNT
            </button>
          </form>
          <div className="login-button">
            Already have an account?{' '}
            <Link className="login-button green-link" to="/v2/login">
              Log In
            </Link>
          </div>
        </div>
      </div>
      <div className="col-md-5">
        <OrganizationTypeSection orgType={orgType} />
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { authenticated, currentUser } = state.session;
  const organizationRequired = requireOrganizationData(currentUser, authenticated);

  if (authenticated && currentUser && organizationRequired) {
    ownProps.history.push('/v2/create-event/organization');
  }

  if (authenticated && currentUser && !organizationRequired) {
    ownProps.history.push('/dashboard');
  }

  return {
    user: state.registration.user,
    validation: state.registration.postEventAccountValidation,
    flash: state.flash,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateField: (field, value) => dispatch(registrationFieldUpdate(field, value)),
    validateField: field => dispatch(postEventAccountFieldValidate(field)),
    setRole: role => dispatch(setRole(role)),
    registrationPostEvent: (history, location) =>
      dispatch(registrationPostEvent({ history, location, step: 'event-intro' })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
